import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as PresentationServiceModule from '../services/PresentationService';
import html2pdf from 'html2pdf.js';

const presentationService = PresentationServiceModule.default;
type Presentation = PresentationServiceModule.Presentation;

const PresentationView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [presentation, setPresentation] = useState<Presentation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showContextFiles, setShowContextFiles] = useState(true);

  useEffect(() => {
    const fetchPresentation = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await presentationService.getPresentation(id);
        if (data) {
          setPresentation(data);
          setError(null);
        } else {
          setError('Presentation not found');
        }
      } catch (err) {
        console.error('Error fetching presentation:', err);
        setError('Failed to load presentation. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPresentation();
  }, [id]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleEdit = () => {
    if (id) {
      navigate(`/edit/${id}`);
    }
  };

  const handleExportPDF = () => {
    if (!presentation) return;
    
    const element = document.getElementById('presentation-content');
    if (!element) return;
    
    const opt = {
      margin: 1,
      filename: `${presentation.title}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
    };
    
    html2pdf().set(opt).from(element).save();
  };

  const handleDownloadHTML = () => {
    if (!presentation) return;
    
    const blob = new Blob([presentation.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${presentation.title}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Format date for display
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Get appropriate icon for file type
  const getFileIcon = (file: PresentationServiceModule.FileWithContent) => {
    if (file.type.match('application/pdf')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-red-900/30 border border-red-500/30 text-red-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 18H17V16H7V18Z" fill="currentColor" />
            <path d="M17 14H7V12H17V14Z" fill="currentColor" />
            <path fillRule="evenodd" clipRule="evenodd" d="M6 2C4.34315 2 3 3.34315 3 5V19C3 20.6569 4.34315 22 6 22H18C19.6569 22 21 20.6569 21 19V9C21 5.13401 17.866 2 14 2H6ZM6 4H13V9H19V19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V5C5 4.44772 5.44772 4 6 4ZM15 4.10002C16.6113 4.4271 17.9413 5.52906 18.584 7H15V4.10002Z" fill="currentColor" />
          </svg>
        </div>
      );
    } else if (file.type.match('text/') || file.name.endsWith('.md') || file.type.match('application/json')) {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-900/30 border border-blue-500/30 text-blue-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3 5C3 3.34315 4.34315 2 6 2H14C14.2652 2 14.5196 2.10536 14.7071 2.29289L20.7071 8.29289C20.8946 8.48043 21 8.73478 21 9V19C21 20.6569 19.6569 22 18 22H6C4.34315 22 3 20.6569 3 19V5ZM13 4H6C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10H14C13.4477 10 13 9.55228 13 9V4ZM15 4.41421V8H18.5858L15 4.41421Z" fill="currentColor" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-800 border border-gray-700 text-gray-400">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M3 5C3 3.34315 4.34315 2 6 2H14C14.2652 2 14.5196 2.10536 14.7071 2.29289L20.7071 8.29289C20.8946 8.48043 21 8.73478 21 9V19C21 20.6569 19.6569 22 18 22H6C4.34315 22 3 20.6569 3 19V5ZM13 4H6C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V10H14C13.4477 10 13 9.55228 13 9V4ZM15 4.41421V8H18.5858L15 4.41421Z" fill="currentColor" />
          </svg>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center h-64">
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-r from-violet-600 to-teal-600 rounded-full opacity-70 blur-lg animate-pulse"></div>
          <div className="relative w-12 h-12 border-4 border-t-transparent border-teal-400 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error || !presentation) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-300">
          {error || 'Presentation not found'}
        </div>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <button
              onClick={handleBack}
              className="mb-4 text-white/70 hover:text-white flex items-center transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-white">{presentation.title}</h1>
            <p className="text-gray-400 mt-1">
              Last updated {formatDate(presentation.updatedAt)}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-gradient-to-br from-violet-600 to-violet-800 text-white rounded-lg hover:from-violet-500 hover:to-violet-700 transition-all duration-300 flex items-center shadow-lg shadow-violet-900/30"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
              Edit
            </button>
            
            <button
              onClick={handleExportPDF}
              className="px-4 py-2 bg-gradient-to-br from-teal-600 to-teal-800 text-white rounded-lg hover:from-teal-500 hover:to-teal-700 transition-all duration-300 flex items-center shadow-lg shadow-teal-900/30"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
              Export PDF
            </button>
            
            <button
              onClick={handleDownloadHTML}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
              </svg>
              Download HTML
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
              </svg>
              Fullscreen
            </button>
          </div>
        </div>
        
        {presentation.contextFiles && presentation.contextFiles.length > 0 && (
          <div className="mb-6">
            <button 
              onClick={() => setShowContextFiles(!showContextFiles)}
              className="flex items-center mb-3 w-full text-left hover:bg-white/5 rounded-lg p-2 transition-colors duration-200"
            >
              <div className="rounded-lg w-8 h-8 bg-teal-900/30 flex items-center justify-center mr-3 border border-teal-400/20">
                <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-medium text-white flex items-center">
                Context Files
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-teal-900/40 text-teal-300 border border-teal-500/20">
                  {presentation.contextFiles.length}
                </span>
              </h3>
              <svg 
                className={`w-5 h-5 ml-auto transition-transform duration-300 text-teal-400 ${showContextFiles ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            
            {showContextFiles && (
              <div className="glassmorphism rounded-xl border border-white/10 overflow-hidden transition-all duration-300">
                <div className="grid gap-1 p-2">
                  {presentation.contextFiles.map((file) => (
                    <div 
                      key={file.id} 
                      className="group p-3 hover:bg-white/5 rounded-lg transition-all duration-200 border border-white/5"
                    >
                      <div className="flex items-start">
                        {getFileIcon(file)}
                        
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate group-hover:text-teal-300 transition-colors duration-200">
                            {file.name}
                          </p>
                          <div className="flex items-center mt-1 flex-wrap gap-2">
                            <span className="text-xs text-gray-400">
                              {formatFileSize(file.size)}
                            </span>
                            <span className="flex items-center text-xs text-gray-500">
                              <span 
                                className="inline-block w-2 h-2 rounded-full mr-1.5" 
                                style={{
                                  backgroundColor: 
                                    file.type.match('application/pdf') ? '#ef4444' : 
                                    file.type.match('text/html') ? '#3b82f6' :
                                    file.type.match('text/plain') ? '#8b5cf6' :
                                    file.name.endsWith('.md') ? '#10b981' :
                                    file.type.match('application/json') ? '#f59e0b' :
                                    '#6b7280'
                                }}
                              ></span>
                              {file.type.match('application/pdf') ? 'PDF' : 
                               file.type.match('text/html') ? 'HTML' :
                               file.type.match('text/plain') ? 'Text' :
                               file.name.endsWith('.md') ? 'Markdown' :
                               file.type.match('application/json') ? 'JSON' :
                               'Document'}
                            </span>
                            {file.tokenCount && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-violet-900/30 text-violet-400 border border-violet-500/20">
                                {Math.round(file.tokenCount).toLocaleString()} tokens
                              </span>
                            )}
                          </div>
                          {file.summary && (
                            <p className="text-xs text-gray-400 mt-1.5 line-clamp-1">
                              {file.summary}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="glassmorphism rounded-xl overflow-hidden border border-white/10 shadow-2xl mb-8">
          <div className="bg-gray-900/80 border-b border-white/5 p-4 flex justify-between items-center">
            <div className="flex space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <div className="text-white/70 text-sm font-medium">Presentation Preview</div>
            <div></div>
          </div>
          
          <div className="bg-white h-[600px] overflow-auto">
            <div 
              id="presentation-content"
              dangerouslySetInnerHTML={{ __html: presentation.html }} 
            />
          </div>
        </div>
      </div>
      
      {isFullscreen && (
        <div className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-6xl h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl relative">
            <div className="absolute top-4 right-4 z-10">
              <button 
                onClick={toggleFullscreen}
                className="bg-gray-800/80 hover:bg-gray-700 text-white rounded-full p-2 backdrop-blur-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <iframe
              srcDoc={presentation.html}
              title="Fullscreen Presentation"
              className="w-full h-full border-0"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PresentationView; 